import React, { useState } from 'react';
import { useServices } from '../contexts/ServiceContext';
import './WalletConnectionScreen.css';

const WalletConnectionScreen = ({ onWalletConnected }) => {
  const services = useServices();
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Get wallet service using the correct ServiceFactory method
  const walletService = services?.getWalletService ? services.getWalletService() : null;

  // Debug logging (only on mount, not every render)
  // To debug, uncomment: console.log('🔍 WalletConnectionScreen Debug:', { services, walletService });

  // Try connecting to a specific wallet provider
  const tryProvider = async (provider, name) => {
    try {
      console.log(`🔗 Trying ${name} wallet...`);
      const response = await provider.connect();
      if (response && response.publicKey) {
        const address = response.publicKey.toString();
        console.log(`✅ Connected via ${name}:`, address);
        return { success: true, address, publicKey: response.publicKey };
      }
    } catch (err) {
      console.warn(`⚠️ ${name} connection failed:`, err.message);
      // Re-throw user rejections so we can show the right message
      if (err.message?.includes('User rejected') || err.message?.includes('cancelled')) {
        throw err;
      }
    }
    return null;
  };

  const handleConnectWallet = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      // Build ordered list of available wallet providers
      const providers = [];
      
      if (window.phantom?.solana)  providers.push([window.phantom.solana, 'Phantom']);
      if (window.solana?.isPhantom && !providers.length) providers.push([window.solana, 'Phantom (legacy)']);
      if (window.solflare)         providers.push([window.solflare, 'Solflare']);
      if (window.nightly?.solana)  providers.push([window.nightly.solana, 'Nightly']);
      // Generic window.solana fallback (could be any injected wallet)
      if (window.solana && !window.solana.isPhantom) providers.push([window.solana, 'Solana Wallet']);

      if (providers.length === 0) {
        setError('No Solana wallet found. Please install Phantom or Solflare.');
        return;
      }

      // Try each provider in order until one succeeds
      for (const [provider, name] of providers) {
        const result = await tryProvider(provider, name);
        if (result) {
          onWalletConnected(result);
          return;
        }
      }

      setError('Could not connect to any wallet. Please unlock your wallet and try again.');
    } catch (err) {
      console.error('❌ Wallet connection error:', err);
      if (err.message?.includes('User rejected') || err.message?.includes('cancelled')) {
        setError('Connection cancelled by user');
      } else {
        setError(err.message || 'Failed to connect wallet. Try refreshing the page.');
      }
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="wallet-connection-screen">
      <div className="wallet-connection-container">
        <div className="wallet-connection-header">
          <div className="logo-section">
            🚀
          </div>
          <h1>Welcome to Znoospace!</h1>
          <p className="subtitle">
            The decentralized social media platform powered by Solana blockchain
          </p>
        </div>

        <div className="wallet-connection-content">
          <div className="connection-info">
            <h2>Connect Your Wallet</h2>
            <p>
              To get started, you'll need to connect a Solana wallet. This allows you to:
            </p>
            <ul className="benefits-list">
              <li>✨ Create and manage your profile on-chain</li>
              <li>📝 Post messages to the blockchain</li>
              <li>💎 Use ZNOOZINU tokens for premium features</li>
              <li>🗳️ Participate in DAO governance</li>
              <li>🔒 Own your data and identity</li>
            </ul>
          </div>

          <div className="wallet-options">
            <div className="wallet-security-notice">
              <div className="security-icon">🔐</div>
              <div className="security-text">
                <strong>Your privacy matters</strong>
                <p>We only request read access to your wallet address. No transactions will be made without your explicit approval.</p>
              </div>
            </div>

            <button 
              className="connect-wallet-btn"
              onClick={handleConnectWallet}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <div className="spinner"></div>
                  Connecting...
                </>
              ) : (
                <>
                  � Connect Solana Wallet
                </>
              )}
            </button>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <div className="wallet-help">
              <p>Don't have a Solana wallet?</p>
              <a 
                href="https://phantom.app/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="help-link"
              >
                Phantom
              </a>
              {' · '}
              <a 
                href="https://solflare.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="help-link"
              >
                Solflare
              </a>
              {' · '}
              <a 
                href="https://nightly.app/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="help-link"
              >
                Nightly
              </a>
            </div>
          </div>
        </div>

        <div className="wallet-connection-footer">
          <p className="disclaimer">
            By connecting your wallet, you agree to our terms of service and privacy policy.
            Your wallet connection is secure and encrypted.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WalletConnectionScreen;
